import { useState } from 'react';
import { UserPlus01, Copy01, Link01, Trash01 } from '@untitledui/icons';
import { ModalOverlay, Modal, Dialog } from '@/components/application/modals/modal';
import { Tabs, TabList, TabPanel, Tab } from '@/components/application/tabs/tabs';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Checkbox } from '@/components/base/checkbox/checkbox';
import { CloseButton } from '@/components/base/buttons/close-button';
import { toast } from '@/lib/toast-service';
import {
  useCreateAccessToken,
  useDocumentAccessTokens,
  useRevokeAccessToken,
  type DocumentAccessToken,
} from '@/hooks/api/use-document-access';

interface ShareDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
}

const EXPIRATION_OPTIONS = [
  { id: '24', label: '24 hours' },
  { id: '168', label: '7 days' },
  { id: '720', label: '30 days' },
];

function statusColor(status: DocumentAccessToken['status']) {
  switch (status) {
    case 'active': return 'success' as const;
    case 'expired': return 'warning' as const;
    case 'revoked': return 'error' as const;
    default: return 'gray' as const;
  }
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ShareDocumentModal({ isOpen, onClose, documentId }: ShareDocumentModalProps) {
  const [activeTab, setActiveTab] = useState<string>('invite');

  // Invite state
  const [email, setEmail] = useState('');
  const [expiresIn, setExpiresIn] = useState('168');
  const [canView, setCanView] = useState(true);
  const [canComment, setCanComment] = useState(true);

  // Link state
  const [linkExpiresIn, setLinkExpiresIn] = useState('168');
  const [linkCanView, setLinkCanView] = useState(true);
  const [linkCanComment, setLinkCanComment] = useState(true);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);

  // Hooks
  const createToken = useCreateAccessToken();
  const { data: tokens = [], isLoading: tokensLoading } = useDocumentAccessTokens(documentId, isOpen);
  const revokeToken = useRevokeAccessToken();

  const resetInviteForm = () => {
    setEmail('');
    setExpiresIn('168');
    setCanView(true);
    setCanComment(true);
  };

  const handleSendInvite = () => {
    if (!email.trim()) {
      toast.warning('Please enter an email address');
      return;
    }

    createToken.mutate(
      {
        documentId,
        email: email.trim(),
        expires_in_hours: Number(expiresIn),
        can_view: canView,
        can_comment: canComment,
      },
      {
        onSuccess: () => {
          toast.success(`Invite sent to ${email.trim()}`);
          resetInviteForm();
          setActiveTab('access');
        },
        onError: (err) => {
          toast.error(err);
        },
      }
    );
  };

  const handleRevoke = (token: DocumentAccessToken) => {
    revokeToken.mutate(
      { tokenId: token.access_token, documentId },
      {
        onSuccess: () => toast.success('Access revoked'),
        onError: (err) => toast.error(err),
      }
    );
  };

  const handleGenerateLink = () => {
    createToken.mutate(
      {
        documentId,
        email: 'link-share@evemind.app',
        expires_in_hours: Number(linkExpiresIn),
        can_view: linkCanView,
        can_comment: linkCanComment,
      },
      {
        onSuccess: (result) => {
          const url = `${window.location.origin}/review/${result.access_token}`;
          setGeneratedLink(url);
          toast.success('Link generated');
        },
        onError: (err) => toast.error(err),
      }
    );
  };

  const handleCopyLink = async () => {
    if (!generatedLink) return;
    try {
      await navigator.clipboard.writeText(generatedLink);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleClose = () => {
    resetInviteForm();
    setGeneratedLink(null);
    onClose();
  };

  const tabs = [
    { id: 'invite', label: 'Invite' },
    { id: 'access', label: 'Access' },
    { id: 'link', label: 'Link' },
  ];

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && handleClose()} isDismissable>
      <Modal className="max-w-lg">
        <Dialog>
          <div className="w-full rounded-xl bg-primary shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-secondary px-5 py-4">
              <h2 className="text-lg font-semibold text-primary">Share Document</h2>
              <CloseButton onPress={handleClose} />
            </div>

            {/* Tabs */}
            <Tabs selectedKey={activeTab} onSelectionChange={(key) => setActiveTab(key as string)}>
              <div className="px-5 pt-4">
                <TabList type="underline" size="sm" items={tabs}>
                  {(item) => <Tab id={item.id}>{item.label}</Tab>}
                </TabList>
              </div>

              {/* Invite Tab */}
              <TabPanel id="invite" className="px-5 py-4">
                <div className="space-y-4">
                  <Input
                    label="Email address"
                    placeholder="colleague@company.com"
                    value={email}
                    onChange={setEmail}
                    type="email"
                    icon={UserPlus01}
                  />

                  <Select
                    label="Expires in"
                    selectedKey={expiresIn}
                    onSelectionChange={(key) => setExpiresIn(key as string)}
                    items={EXPIRATION_OPTIONS}
                    size="sm"
                  >
                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                  </Select>

                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium text-secondary mb-1">Permissions</legend>
                    <Checkbox
                      isSelected={canView}
                      onChange={setCanView}
                      label="Can view document"
                      size="sm"
                    />
                    <Checkbox
                      isSelected={canComment}
                      onChange={setCanComment}
                      label="Can comment"
                      size="sm"
                    />
                  </fieldset>

                  <Button
                    color="primary"
                    className="w-full"
                    onPress={handleSendInvite}
                    isLoading={createToken.isPending}
                    iconLeading={UserPlus01}
                  >
                    Send invite
                  </Button>
                </div>
              </TabPanel>

              {/* Access Tab */}
              <TabPanel id="access" className="px-5 py-4">
                {tokensLoading ? (
                  <p className="text-sm text-tertiary text-center py-6">Loading...</p>
                ) : tokens.length === 0 ? (
                  <div className="text-center py-8">
                    <UserPlus01 className="size-8 text-quaternary mx-auto mb-2" />
                    <p className="text-sm text-tertiary">No one has access yet</p>
                    <p className="text-xs text-quaternary mt-1">
                      Use the Invite tab to share this document
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {tokens.map((token) => (
                      <div
                        key={token.suggestion_access_id}
                        className="flex items-center justify-between rounded-lg border border-secondary px-3 py-2.5"
                      >
                        <div className="flex-1 min-w-0 mr-3">
                          <p className="text-sm text-primary truncate">{token.granted_to_email}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge size="sm" color={statusColor(token.status)}>
                              {token.status}
                            </Badge>
                            <span className="text-xs text-quaternary">
                              Expires {formatDate(token.token_expires_at)}
                            </span>
                          </div>
                        </div>
                        {token.status === 'active' && (
                          <Button
                            size="sm"
                            color="primary-destructive"
                            iconLeading={Trash01}
                            onPress={() => handleRevoke(token)}
                            isLoading={revokeToken.isPending}
                            noTextPadding
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabPanel>

              {/* Link Tab */}
              <TabPanel id="link" className="px-5 py-4">
                <div className="space-y-4">
                  <Select
                    label="Link expires in"
                    selectedKey={linkExpiresIn}
                    onSelectionChange={(key) => setLinkExpiresIn(key as string)}
                    items={EXPIRATION_OPTIONS}
                    size="sm"
                  >
                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                  </Select>

                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium text-secondary mb-1">Permissions</legend>
                    <Checkbox
                      isSelected={linkCanView}
                      onChange={setLinkCanView}
                      label="Can view document"
                      size="sm"
                    />
                    <Checkbox
                      isSelected={linkCanComment}
                      onChange={setLinkCanComment}
                      label="Can comment"
                      size="sm"
                    />
                  </fieldset>

                  {!generatedLink ? (
                    <Button
                      color="primary"
                      className="w-full"
                      onPress={handleGenerateLink}
                      isLoading={createToken.isPending}
                      iconLeading={Link01}
                    >
                      Generate link
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-secondary">Share link</label>
                      <div className="flex gap-2">
                        <div className="flex-1 rounded-md border border-secondary bg-secondary px-3 py-2 text-sm text-primary truncate select-all">
                          {generatedLink}
                        </div>
                        <Button
                          size="sm"
                          color="secondary"
                          iconLeading={Copy01}
                          onPress={handleCopyLink}
                          noTextPadding
                        />
                      </div>
                      <Button
                        color="tertiary"
                        size="sm"
                        className="w-full"
                        iconLeading={Link01}
                        onPress={() => {
                          setGeneratedLink(null);
                        }}
                      >
                        Generate new link
                      </Button>
                    </div>
                  )}
                </div>
              </TabPanel>
            </Tabs>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
